import { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Search, Package, X, Pencil, Trash2, Eye, Copy, 
  Upload, Image as ImageIcon, ChevronDown, Check, Loader2,
  Truck, Store, Clock, Euro, Box, Tag, Star, AlertCircle,
  FileText, Video, Award, Weight, Ruler, Globe, Hash,
  Sparkles, ExternalLink, CheckCircle, XCircle, Minus,
  ToggleLeft, ToggleRight, ChevronRight
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import ImageUploader from '../../../components/ImageUploader';
import { 
  DECLINAISONS_PAR_RAYON, 
  MAX_DECLINAISONS_DEFAULT, 
  initializeDeclinaisons 
} from '../../../data/declinaisons-par-rayon';

import { API_URL } from '../../../config/api';

// Labels pour les modes de livraison
const deliveryModeLabels = {
  colissimo: { label: 'Colissimo', icon: '📦', description: 'Livraison standard La Poste' },
  chronopost: { label: 'Chronopost', icon: '🚀', description: 'Livraison express 24h' },
  transporteur_palette: { label: 'Transporteur palette', icon: '🚛', description: 'Pour les gros volumes' },
  livraison_artisan: { label: 'Livraison par artisan', icon: '🔧', description: 'Livraison directe par le pro' },
  retrait_sur_place: { label: 'Retrait sur place', icon: '🏪', description: 'À récupérer en magasin' },
  click_and_collect: { label: 'Click & Collect', icon: '🛒', description: 'Commandez en ligne, retirez en magasin' }
};

// Labels pour les types de frais
const shippingCostTypeLabels = {
  free: 'Gratuit',
  fixed: 'Forfait fixe',
  by_weight: 'Selon le poids'
};

// Labels pour les zones
const shippingZoneLabels = {
  france: 'France métropolitaine',
  dom_tom: 'DOM-TOM',
  europe: 'Europe',
  mondial: 'Monde entier'
};

// Labels pour les certifications
const certificationLabels = {
  nf: { label: 'NF', description: 'Norme Française' },
  ce: { label: 'CE', description: 'Conformité Européenne' },
  rge: { label: 'RGE', description: 'Reconnu Garant de l\'Environnement' },
  qualibat: { label: 'Qualibat', description: 'Qualification Bâtiment' },
  qualipac: { label: 'QualiPAC', description: 'Qualification Pompe à Chaleur' },
  qualifelec: { label: 'Qualifelec', description: 'Qualification Électricité' }
};

// Labels pour les statuts
const statusLabels = {
  draft: { label: 'Brouillon', color: 'bg-slate-100 text-slate-600' },
  active: { label: 'Publié', color: 'bg-green-100 text-green-600' },
  paused: { label: 'En pause', color: 'bg-yellow-100 text-yellow-600' },
  out_of_stock: { label: 'Rupture', color: 'bg-red-100 text-red-600' },
  archived: { label: 'Archivé', color: 'bg-slate-100 text-slate-400' }
};

// Types de transaction pour les Pros: Achat et Location UNIQUEMENT (pas d'échange ni don)
const TRANSACTION_TYPES_PRO = [
  { id: 'achat', label: 'Vente', description: 'Vendre ce produit', icon: '💰', color: '#F97316' },
  { id: 'location', label: 'Location', description: 'Louer ce matériel', icon: '📅', color: '#3B82F6' },
];

// Fonction pour générer un slug
const generateSlug = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
};

// Composant de prévisualisation Card
const ProductCardPreview = ({ formData }) => {
  const primaryPhoto = formData.photos?.[0]?.url;
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      {/* Image */}
      <div className="relative aspect-square bg-slate-100">
        {primaryPhoto ? (
          <img src={primaryPhoto} alt={formData.name || 'Produit'} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-12 h-12 text-slate-300" />
          </div>
        )}
        {formData.delivery?.cost_type === 'free' && (
          <span className="absolute top-2 left-2 px-2 py-1 bg-green-500 text-white text-xs font-medium rounded-full">
            Livraison gratuite
          </span>
        )}
        {formData.declinaisons?.filter(d => d.enabled && d.selectedValues?.length > 0).length > 0 && (
          <span className="absolute top-2 right-2 px-2 py-1 bg-slate-800 text-white text-xs font-medium rounded-full">
            {formData.declinaisons.filter(d => d.enabled && d.selectedValues?.length > 0).length} déclinaisons
          </span>
        )}
      </div>
      
      {/* Content */}
      <div className="p-4">
        {/* Category */}
        <p className="text-xs text-rm-primary font-medium mb-1 truncate">
          {formData.rayon_name || 'Rayon'} › {formData.category_name || 'Catégorie'}
        </p>
        
        {/* Title */}
        <h3 className="font-semibold text-rm-text line-clamp-2 mb-1 min-h-[2.5rem]">
          {formData.name || 'Nom du produit'}
        </h3>
        
        {/* Brand */}
        {formData.brand && (
          <p className="text-xs text-slate-500 mb-2">{formData.brand}</p>
        )}
        
        {/* Price */}
        <div className="flex items-baseline gap-2 mb-2">
          <span className="text-xl font-bold text-rm-text">
            {formData.price_ttc ? `${parseFloat(formData.price_ttc).toFixed(2)} €` : '0,00 €'}
          </span>
          <span className="text-xs text-slate-500">TTC</span>
        </div>
        
        {/* Rating */}
        <div className="flex items-center gap-1 mb-2">
          {[1, 2, 3, 4, 5].map(star => (
            <Star key={star} className="w-4 h-4 text-slate-300" />
          ))}
          <span className="text-xs text-slate-500 ml-1">(0 avis)</span>
        </div>
        
        {/* Stock */}
        <p className={`text-xs font-medium ${
          formData.stock === -1 ? 'text-blue-600' :
          formData.stock > 0 ? 'text-green-600' : 'text-red-600'
        }`}>
          {formData.stock === -1 ? '✓ Sur commande' : 
           formData.stock > 0 ? `✓ En stock (${formData.stock})` : '✗ Rupture de stock'}
        </p>
      </div>
    </div>
  );
};

// Composant Score SEO
const SEOScoreIndicator = ({ formData }) => {
  const checks = useMemo(() => {
    const results = [];
    
    // Titre SEO
    const seoTitle = formData.seo_title || formData.name || '';
    if (seoTitle.length >= 30 && seoTitle.length <= 60) {
      results.push({ label: 'Titre SEO optimal (30-60 car.)', status: 'good' });
    } else if (seoTitle.length > 0) {
      results.push({ label: `Titre SEO: ${seoTitle.length} car. (idéal: 30-60)`, status: seoTitle.length < 30 ? 'warning' : 'warning' });
    } else {
      results.push({ label: 'Titre SEO manquant', status: 'bad' });
    }
    
    // Meta description
    const seoDesc = formData.seo_description || formData.description_short || '';
    if (seoDesc.length >= 120 && seoDesc.length <= 160) {
      results.push({ label: 'Meta description optimale (120-160 car.)', status: 'good' });
    } else if (seoDesc.length > 0) {
      results.push({ label: `Meta description: ${seoDesc.length} car. (idéal: 120-160)`, status: 'warning' });
    } else {
      results.push({ label: 'Meta description manquante', status: 'bad' });
    }
    
    // URL slug
    if (formData.seo_slug && formData.seo_slug.length > 0) {
      results.push({ label: 'URL personnalisée définie', status: 'good' });
    } else if (formData.name) {
      results.push({ label: 'URL générée automatiquement', status: 'warning' });
    } else {
      results.push({ label: 'URL non définie', status: 'bad' });
    }
    
    // Mots-clés
    if (formData.seo_keywords?.length >= 3) {
      results.push({ label: `${formData.seo_keywords.length} mots-clés définis`, status: 'good' });
    } else if (formData.seo_keywords?.length > 0) {
      results.push({ label: `${formData.seo_keywords.length} mot(s)-clé(s) (min recommandé: 3)`, status: 'warning' });
    } else {
      results.push({ label: 'Aucun mot-clé défini', status: 'bad' });
    }
    
    // Photos
    if (formData.photos?.length >= 3) {
      results.push({ label: `${formData.photos.length} photos (excellent)`, status: 'good' });
    } else if (formData.photos?.length > 0) {
      results.push({ label: `${formData.photos.length} photo(s) (3+ recommandées)`, status: 'warning' });
    } else {
      results.push({ label: 'Aucune photo', status: 'bad' });
    }
    
    // Description longue
    if (formData.description_long?.length >= 300) {
      results.push({ label: 'Description détaillée complète', status: 'good' });
    } else if (formData.description_long?.length > 0) {
      results.push({ label: 'Description courte (300+ car. recommandés)', status: 'warning' });
    } else {
      results.push({ label: 'Description longue manquante', status: 'bad' });
    }
    
    return results;
  }, [formData]);
  
  const score = useMemo(() => {
    const good = checks.filter(c => c.status === 'good').length;
    return Math.round((good / checks.length) * 100);
  }, [checks]);
  
  const scoreColor = score >= 80 ? 'text-green-600' : score >= 50 ? 'text-yellow-600' : 'text-red-600';
  const scoreBg = score >= 80 ? 'bg-green-100' : score >= 50 ? 'bg-yellow-100' : 'bg-red-100';
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-semibold text-rm-text flex items-center gap-2">
          <Globe className="w-5 h-5" />
          Score SEO
        </h4>
        <span className={`text-2xl font-bold ${scoreColor}`}>{score}%</span>
      </div>
      
      {/* Progress bar */}
      <div className="w-full h-2 bg-slate-200 rounded-full mb-4 overflow-hidden">
        <div 
          className={`h-full transition-all duration-500 ${scoreBg.replace('100', '500')}`}
          style={{ width: `${score}%` }}
        />
      </div>
      
      {/* Checklist */}
      <div className="space-y-2">
        {checks.map((check, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            {check.status === 'good' ? (
              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
            ) : check.status === 'warning' ? (
              <Minus className="w-4 h-4 text-yellow-500 flex-shrink-0" />
            ) : (
              <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            )}
            <span className={
              check.status === 'good' ? 'text-green-700' :
              check.status === 'warning' ? 'text-yellow-700' : 'text-red-700'
            }>{check.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Composant prévisualisation Google
const GooglePreview = ({ formData }) => {
  const title = formData.seo_title || formData.name || 'Titre du produit';
  const description = formData.seo_description || formData.description_short || 'Description du produit...';
  const slug = formData.seo_slug || generateSlug(formData.name) || 'produit';
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <h4 className="font-semibold text-rm-text mb-3 flex items-center gap-2">
        <Search className="w-5 h-5" />
        Aperçu Google
      </h4>
      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
        <p className="text-sm text-green-700 mb-1 truncate">
          abracadabati.com › marketplace › {slug}
        </p>
        <p className="text-lg text-blue-800 hover:underline cursor-pointer font-medium mb-1 line-clamp-1">
          {title.substring(0, 60)}{title.length > 60 ? '...' : ''}
        </p>
        <p className="text-sm text-slate-600 line-clamp-2">
          {description.substring(0, 160)}{description.length > 160 ? '...' : ''}
        </p>
      </div>
    </div>
  );
};

export default function ProduitsPage() {
  const { user } = useOutletContext() || {};
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterRayon, setFilterRayon] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState('card'); // 'card' ou 'seo'
  
  // Rayons data pour les dropdowns
  const [rayonsData, setRayonsData] = useState([]);
  const [loadingRayons, setLoadingRayons] = useState(false);
  
  // Stats
  const [stats, setStats] = useState(null);
  
  // Form data avec tous les champs du schéma
  const [formData, setFormData] = useState({
    // Obligatoires
    name: '',
    rayon_id: '',
    rayon_name: '',
    sous_rayon_id: '',
    sous_rayon_name: '',
    category_id: '',
    category_name: '',
    price_ttc: '',
    stock: 0,
    description_short: '',
    description_long: '',
    photos: [],
    
    // Type de transaction (Pro: achat, location, don)
    transaction_type: 'achat',
    
    // Optionnels
    sku: '',
    brand: '',
    weight_kg: '',
    dimensions_l: '',
    dimensions_w: '',
    dimensions_h: '',
    material: '',
    warranty_months: '',
    certifications: [],
    pdf_technical_url: '',
    video_url: '',
    
    // Variations (ancien système)
    variations: [],
    
    // Déclinaisons pré-remplies par rayon (nouveau système)
    declinaisons: [],
    
    // Livraison
    delivery: {
      available: true,
      modes: [],
      estimated_delay: '',
      cost_type: 'fixed',
      cost_amount: '',
      cost_per_kg: '',
      free_shipping_threshold: '',
      zones: ['france']
    },
    
    // SEO
    seo_title: '',
    seo_description: '',
    seo_slug: '',
    seo_keywords: [],
    
    // Statut
    status: 'draft'
  });
  
  // État pour afficher toutes les déclinaisons
  const [showAllDeclinaisons, setShowAllDeclinaisons] = useState(false);

  // Derived state pour les dropdowns en cascade
  const selectedRayon = rayonsData.find(r => r.id === formData.rayon_id);
  const selectedSousRayon = selectedRayon?.sous_rayons?.find(sr => sr.id === formData.sous_rayon_id);

  useEffect(() => {
    fetchProducts();
    fetchRayons();
    fetchStats();
  }, [filterStatus, filterRayon]);

  const fetchRayons = async () => {
    setLoadingRayons(true);
    try {
      const response = await fetch(`${API_URL}/api/marketplace/rayons/full`);
      if (response.ok) {
        const data = await response.json();
        setRayonsData(data);
      }
    } catch (error) {
      console.error('Error fetching rayons:', error);
    } finally {
      setLoadingRayons(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // DONNÉES DÉMO POUR LE MODE SANS AUTHENTIFICATION
  // ═══════════════════════════════════════════════════════════════
  const DEMO_PRODUCTS = [
    { id: 'p1', name: 'Tableau électrique 2 rangées', rayon_name: 'Électricité', sous_rayon_name: 'Tableaux électriques', category_name: 'Coffrets', price_ttc: 89.90, stock: 15, status: 'active', photos: [{url: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=200'}], sales_count: 12 },
    { id: 'p2', name: 'Mitigeur cuisine chromé', rayon_name: 'Plomberie', sous_rayon_name: 'Robinetterie cuisine', category_name: 'Mitigeurs évier', price_ttc: 129.00, stock: 8, status: 'active', photos: [{url: 'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=200'}], sales_count: 25 },
    { id: 'p3', name: 'Tondeuse thermique 46cm', rayon_name: 'Jardin & Extérieur', sous_rayon_name: 'Tondeuses', category_name: 'Thermiques', price_ttc: 349.00, stock: 3, status: 'active', photos: [{url: 'https://images.unsplash.com/photo-1558171813-4c088753af8f?w=200'}], sales_count: 7 },
    { id: 'p4', name: 'Robot piscine électrique', rayon_name: 'Piscine & Spa', sous_rayon_name: 'Robots & nettoyage', category_name: 'Robots électriques', price_ttc: 599.00, stock: 2, status: 'active', photos: [{url: 'https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?w=200'}], sales_count: 4 },
    { id: 'p5', name: 'Peinture acrylique blanche 10L', rayon_name: 'Peinture & Décoration', sous_rayon_name: 'Peinture murs', category_name: 'Acrylique', price_ttc: 59.90, stock: 25, status: 'active', photos: [{url: 'https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=200'}], sales_count: 45 },
    { id: 'p6', name: 'Carrelage imitation bois 60x60', rayon_name: 'Carrelage & Sols', sous_rayon_name: 'Carrelage sol intérieur', category_name: 'Imitation bois', price_ttc: 24.90, stock: 120, status: 'active', photos: [{url: 'https://images.unsplash.com/photo-1615971677499-5467cbab01c0?w=200'}], sales_count: 89 },
    { id: 'p7', name: 'Fenêtre PVC 120x140', rayon_name: 'Menuiserie & Fermetures', sous_rayon_name: 'Fenêtres PVC', category_name: 'Standard', price_ttc: 289.00, stock: 0, status: 'out_of_stock', photos: [{url: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=200'}], sales_count: 15 },
    { id: 'p8', name: 'Radiateur inertie 1500W', rayon_name: 'Chauffage', sous_rayon_name: 'Radiateurs électriques', category_name: 'Inertie', price_ttc: 199.00, stock: 10, status: 'active', photos: [{url: 'https://images.unsplash.com/photo-1584568694244-14fbdf83bd30?w=200'}], sales_count: 18 },
  ];

  const DEMO_STATS = {
    total_products: 8,
    active_products: 7,
    out_of_stock: 1,
    total_sales: 215,
    revenue_month: 12450
  };

  const fetchProducts = async () => {
    const token = localStorage.getItem('client_token');
    
    // MODE DÉMO
    if (!token) {
      setProducts(DEMO_PRODUCTS);
      setLoading(false);
      return;
    }

    try {
      let url = `${API_URL}/api/cockpit/products`;
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterRayon) params.append('rayon_id', filterRayon);
      if (searchQuery) params.append('search', searchQuery);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts(DEMO_PRODUCTS);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    const token = localStorage.getItem('client_token');
    
    // MODE DÉMO
    if (!token) {
      setStats(DEMO_STATS);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/cockpit/products-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats(DEMO_STATS);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // Validation
    if (!formData.name) {
      toast.error('Le nom du produit est requis');
      setCurrentStep(1);
      return;
    }
    if (!formData.rayon_id || !formData.sous_rayon_id || !formData.category_id) {
      toast.error('Veuillez sélectionner un rayon, sous-rayon et catégorie');
      setCurrentStep(1);
      return;
    }
    if (!formData.price_ttc || parseFloat(formData.price_ttc) <= 0) {
      toast.error('Le prix TTC doit être supérieur à 0');
      setCurrentStep(1);
      return;
    }
    if (!formData.description_short) {
      toast.error('La description courte est requise');
      setCurrentStep(1);
      return;
    }
    if (!formData.description_long) {
      toast.error('La description longue est requise');
      setCurrentStep(1);
      return;
    }

    setSaving(true);
    const token = localStorage.getItem('client_token');
    
    try {
      const payload = {
        name: formData.name,
        rayon_id: formData.rayon_id,
        rayon_name: formData.rayon_name,
        sous_rayon_id: formData.sous_rayon_id,
        sous_rayon_name: formData.sous_rayon_name,
        category_id: formData.category_id,
        category_name: formData.category_name,
        price_ttc: parseFloat(formData.price_ttc),
        stock: parseInt(formData.stock) || 0,
        description_short: formData.description_short,
        description_long: formData.description_long,
        photos: formData.photos.map((p, i) => ({
          url: p.url,
          is_primary: i === 0,
          order: i,
          alt_text: p.alt_text || formData.name
        })),
        sku: formData.sku || null,
        brand: formData.brand || null,
        weight_kg: formData.weight_kg ? parseFloat(formData.weight_kg) : null,
        dimensions_l: formData.dimensions_l ? parseFloat(formData.dimensions_l) : null,
        dimensions_w: formData.dimensions_w ? parseFloat(formData.dimensions_w) : null,
        dimensions_h: formData.dimensions_h ? parseFloat(formData.dimensions_h) : null,
        material: formData.material || null,
        warranty_months: formData.warranty_months ? parseInt(formData.warranty_months) : null,
        certifications: formData.certifications,
        pdf_technical_url: formData.pdf_technical_url || null,
        video_url: formData.video_url || null,
        variations: formData.variations.map(v => ({
          name: v.name,
          price_override: v.price_override ? parseFloat(v.price_override) : null,
          stock: parseInt(v.stock) || 0,
          sku: v.sku || null
        })),
        // NB : pas de champ `declinaisons` — inconnu du backend (extra=forbid
        // → 422). Les déclinaisons pré-remplies restent locales tant que le
        // backend ne les gère pas ; seul `variations` est enregistré.
        delivery: {
          available: formData.delivery.available,
          modes: formData.delivery.modes,
          estimated_delay: formData.delivery.estimated_delay || null,
          cost_type: formData.delivery.cost_type,
          cost_amount: formData.delivery.cost_amount ? parseFloat(formData.delivery.cost_amount) : null,
          cost_per_kg: formData.delivery.cost_per_kg ? parseFloat(formData.delivery.cost_per_kg) : null,
          free_shipping_threshold: formData.delivery.free_shipping_threshold ? parseFloat(formData.delivery.free_shipping_threshold) : null,
          zones: formData.delivery.zones
        },
        // SEO
        seo_title: formData.seo_title || null,
        seo_description: formData.seo_description || null,
        seo_slug: formData.seo_slug || generateSlug(formData.name),
        seo_keywords: formData.seo_keywords || [],
        status: formData.status
      };

      const url = editingProduct 
        ? `${API_URL}/api/cockpit/products/${editingProduct.id}`
        : `${API_URL}/api/cockpit/products`;
      
      const method = editingProduct ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(editingProduct ? 'Produit mis à jour !' : 'Produit créé !');
        if (result.synced_to_marketplace) {
          toast.success('Produit synchronisé sur la Marketplace', { duration: 3000 });
        }
        setShowModal(false);
        resetForm();
        fetchProducts();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!confirm('Archiver ce produit ? Il sera retiré de la Marketplace.')) return;

    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/cockpit/products/${productId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Produit archivé');
        fetchProducts();
        fetchStats();
      }
    } catch (error) {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const handleDuplicate = async (productId) => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/cockpit/products/${productId}/duplicate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Produit dupliqué');
        fetchProducts();
        fetchStats();
      }
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleStatusChange = async (productId, newStatus) => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/cockpit/products/${productId}/status?status=${newStatus}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(`Statut changé en "${statusLabels[newStatus]?.label}"`);
        fetchProducts();
        fetchStats();
      }
    } catch (error) {
      toast.error('Erreur lors du changement de statut');
    }
  };

  const openEditModal = async (product) => {
    setEditingProduct(product);
    // Charger les déclinaisons pour ce rayon
    const declinaisons = product.declinaisons || initializeDeclinaisons(product.rayon_id);
    setShowAllDeclinaisons(false);
    setFormData({
      name: product.name || '',
      rayon_id: product.rayon_id || '',
      rayon_name: product.rayon_name || '',
      sous_rayon_id: product.sous_rayon_id || '',
      sous_rayon_name: product.sous_rayon_name || '',
      category_id: product.category_id || '',
      category_name: product.category_name || '',
      price_ttc: product.price_ttc || '',
      stock: product.stock || 0,
      description_short: product.description_short || '',
      description_long: product.description_long || '',
      photos: product.photos || [],
      sku: product.sku || '',
      brand: product.brand || '',
      weight_kg: product.weight_kg || '',
      dimensions_l: product.dimensions_l || '',
      dimensions_w: product.dimensions_w || '',
      dimensions_h: product.dimensions_h || '',
      material: product.material || '',
      warranty_months: product.warranty_months || '',
      certifications: product.certifications || [],
      pdf_technical_url: product.pdf_technical_url || '',
      video_url: product.video_url || '',
      variations: product.variations || [],
      declinaisons: declinaisons,
      delivery: product.delivery || {
        available: true,
        modes: [],
        estimated_delay: '',
        cost_type: 'fixed',
        cost_amount: '',
        cost_per_kg: '',
        free_shipping_threshold: '',
        zones: ['france']
      },
      seo_title: product.seo_title || '',
      seo_description: product.seo_description || '',
      seo_slug: product.seo_slug || '',
      seo_keywords: product.seo_keywords || [],
      status: product.status || 'draft'
    });
    setCurrentStep(1);
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingProduct(null);
    setCurrentStep(1);
    setShowAllDeclinaisons(false);
    setFormData({
      name: '',
      rayon_id: '',
      rayon_name: '',
      sous_rayon_id: '',
      sous_rayon_name: '',
      category_id: '',
      category_name: '',
      price_ttc: '',
      stock: 0,
      description_short: '',
      description_long: '',
      photos: [],
      sku: '',
      brand: '',
      weight_kg: '',
      dimensions_l: '',
      dimensions_w: '',
      dimensions_h: '',
      material: '',
      warranty_months: '',
      certifications: [],
      pdf_technical_url: '',
      video_url: '',
      variations: [],
      declinaisons: [],
      delivery: {
        available: true,
        modes: [],
        estimated_delay: '',
        cost_type: 'fixed',
        cost_amount: '',
        cost_per_kg: '',
        free_shipping_threshold: '',
        zones: ['france']
      },
      seo_title: '',
      seo_description: '',
      seo_slug: '',
      seo_keywords: [],
      status: 'draft'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  // Photo upload handler
  const handlePhotoAdd = () => {
    const url = prompt('URL de l\'image (ou utilisez un service d\'upload)');
    if (url && formData.photos.length < 10) {
      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, { url, is_primary: prev.photos.length === 0 }]
      }));
    }
  };

  const handlePhotoRemove = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  // Variation handlers
  const handleAddVariation = () => {
    setFormData(prev => ({
      ...prev,
      variations: [...prev.variations, { name: '', price_override: '', stock: 0, sku: '' }]
    }));
  };

  const handleVariationChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      variations: prev.variations.map((v, i) => 
        i === index ? { ...v, [field]: value } : v
      )
    }));
  };

  const handleRemoveVariation = (index) => {
    setFormData(prev => ({
      ...prev,
      variations: prev.variations.filter((_, i) => i !== index)
    }));
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // HANDLERS DÉCLINAISONS PRÉ-REMPLIES
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Toggle ON/OFF une déclinaison
  const handleDeclinaisonToggle = (declinaisonId) => {
    setFormData(prev => ({
      ...prev,
      declinaisons: prev.declinaisons.map(d => 
        d.id === declinaisonId ? { ...d, enabled: !d.enabled } : d
      )
    }));
  };

  // Toggle une valeur chip (sélection/désélection)
  const handleChipToggle = (declinaisonId, chipValue) => {
    setFormData(prev => ({
      ...prev,
      declinaisons: prev.declinaisons.map(d => {
        if (d.id !== declinaisonId) return d;
        const newSelectedValues = d.selectedValues.includes(chipValue)
          ? d.selectedValues.filter(v => v !== chipValue)
          : [...d.selectedValues, chipValue];
        return { ...d, selectedValues: newSelectedValues };
      })
    }));
  };

  // Ajouter une valeur personnalisée
  const handleAddCustomValue = (declinaisonId, customValue) => {
    if (!customValue.trim()) return;
    setFormData(prev => ({
      ...prev,
      declinaisons: prev.declinaisons.map(d => {
        if (d.id !== declinaisonId) return d;
        if (d.selectedValues.includes(customValue.trim())) return d;
        return { 
          ...d, 
          selectedValues: [...d.selectedValues, customValue.trim()],
          customValue: ''
        };
      })
    }));
  };

  // Mettre à jour le champ de saisie personnalisé
  const handleCustomValueChange = (declinaisonId, value) => {
    setFormData(prev => ({
      ...prev,
      declinaisons: prev.declinaisons.map(d => 
        d.id === declinaisonId ? { ...d, customValue: value } : d
      )
    }));
  };

  // Afficher toutes les déclinaisons
  const handleShowAllDeclinaisons = () => {
    setShowAllDeclinaisons(true);
    setFormData(prev => ({
      ...prev,
      declinaisons: prev.declinaisons.map(d => ({ ...d, isVisible: true }))
    }));
  };

  // Delivery mode toggle
  const toggleDeliveryMode = (mode) => {
    setFormData(prev => ({
      ...prev,
      delivery: {
        ...prev.delivery,
        modes: prev.delivery.modes.includes(mode)
          ? prev.delivery.modes.filter(m => m !== mode)
          : [...prev.delivery.modes, mode]
      }
    }));
  };

  // Shipping zone toggle
  const toggleShippingZone = (zone) => {
    setFormData(prev => ({
      ...prev,
      delivery: {
        ...prev.delivery,
        zones: prev.delivery.zones.includes(zone)
          ? prev.delivery.zones.filter(z => z !== zone)
          : [...prev.delivery.zones, zone]
      }
    }));
  };

  // Certification toggle
  const toggleCertification = (cert) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.includes(cert)
        ? prev.certifications.filter(c => c !== cert)
        : [...prev.certifications, cert]
    }));
  };

  // Rayon change handler - Charge automatiquement les déclinaisons
  const handleRayonChange = (rayonId) => {
    const rayon = rayonsData.find(r => r.id === rayonId);
    const newDeclinaisons = initializeDeclinaisons(rayonId);
    setShowAllDeclinaisons(false);
    setFormData(prev => ({
      ...prev,
      rayon_id: rayonId,
      rayon_name: rayon?.name || '',
      sous_rayon_id: '',
      sous_rayon_name: '',
      category_id: '',
      category_name: '',
      declinaisons: newDeclinaisons
    }));
  };

  // Sous-rayon change handler
  const handleSousRayonChange = (sousRayonId) => {
    const sousRayon = selectedRayon?.sous_rayons?.find(sr => sr.id === sousRayonId);
    setFormData(prev => ({
      ...prev,
      sous_rayon_id: sousRayonId,
      sous_rayon_name: sousRayon?.name || '',
      category_id: '',
      category_name: ''
    }));
  };

  // Category change handler
  const handleCategoryChange = (categoryId) => {
    const category = selectedSousRayon?.categories?.find(c => c.id === categoryId);
    setFormData(prev => ({
      ...prev,
      category_id: categoryId,
      category_name: category?.name || ''
    }));
  };

  // SEO keyword handlers
  const [keywordInput, setKeywordInput] = useState('');
  
  const handleAddKeyword = (e) => {
    e.preventDefault();
    if (keywordInput.trim() && !formData.seo_keywords.includes(keywordInput.trim())) {
      setFormData(prev => ({
        ...prev,
        seo_keywords: [...prev.seo_keywords, keywordInput.trim()]
      }));
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword) => {
    setFormData(prev => ({
      ...prev,
      seo_keywords: prev.seo_keywords.filter(k => k !== keyword)
    }));
  };

  // Auto-generate SEO
  const generateSEO = () => {
    const name = formData.name || '';
    const brand = formData.brand || '';
    const category = formData.category_name || '';
    
    // Generate SEO title
    let seoTitle = name;
    if (brand) seoTitle = `${name} - ${brand}`;
    if (seoTitle.length > 60) seoTitle = seoTitle.substring(0, 57) + '...';
    
    // Generate SEO description
    let seoDesc = formData.description_short || '';
    if (!seoDesc && formData.description_long) {
      seoDesc = formData.description_long.substring(0, 160);
    }
    
    // Generate slug
    const seoSlug = generateSlug(name);
    
    // Generate keywords
    const keywords = [];
    if (name) keywords.push(...name.toLowerCase().split(' ').filter(w => w.length > 3));
    if (brand) keywords.push(brand.toLowerCase());
    if (category) keywords.push(category.toLowerCase());
    const uniqueKeywords = [...new Set(keywords)].slice(0, 5);
    
    setFormData(prev => ({
      ...prev,
      seo_title: seoTitle,
      seo_description: seoDesc,
      seo_slug: seoSlug,
      seo_keywords: uniqueKeywords
    }));
    
    toast.success('SEO généré automatiquement !');
  };

  const filteredProducts = products.filter(p => {
    const matchesSearch = !searchQuery || 
      p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.brand?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  // Steps configuration
  const steps = [
    { id: 1, label: 'Informations', icon: FileText },
    { id: 2, label: 'Photos', icon: ImageIcon },
    { id: 3, label: 'Variations', icon: Tag },
    { id: 4, label: 'Livraison', icon: Truck },
    { id: 5, label: 'SEO', icon: Globe },
    { id: 6, label: 'Publication', icon: Eye }
  ];

  return (
    <div className="space-y-6" data-testid="produits-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-rm-text">Mes Produits</h1>
          <p className="text-rm-text-secondary">Gérez votre catalogue et publiez sur la Marketplace</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowModal(true); }} 
          className="bg-rm-primary hover:bg-rm-primary/90"
          data-testid="new-product-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau produit
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl p-4 border border-slate-100">
            <p className="text-2xl font-bold text-rm-text">{stats.total_products}</p>
            <p className="text-sm text-rm-text-secondary">Total produits</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-green-100">
            <p className="text-2xl font-bold text-green-600">{stats.active_products}</p>
            <p className="text-sm text-rm-text-secondary">Publiés</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-100">
            <p className="text-2xl font-bold text-slate-600">{stats.draft_products}</p>
            <p className="text-sm text-rm-text-secondary">Brouillons</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-yellow-100">
            <p className="text-2xl font-bold text-yellow-600">{stats.low_stock_products}</p>
            <p className="text-sm text-rm-text-secondary">Stock bas</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-blue-100">
            <div className="flex items-center gap-1">
              <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
              <p className="text-2xl font-bold text-rm-text">{stats.average_rating}</p>
            </div>
            <p className="text-sm text-rm-text-secondary">Note moyenne</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher un produit..."
            className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
            data-testid="search-input"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
          data-testid="status-filter"
        >
          <option value="">Tous les statuts</option>
          {Object.entries(statusLabels).map(([value, { label }]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <select
          value={filterRayon}
          onChange={(e) => setFilterRayon(e.target.value)}
          className="px-4 py-2 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
          data-testid="rayon-filter"
        >
          <option value="">Tous les rayons</option>
          {rayonsData.map((rayon) => (
            <option key={rayon.id} value={rayon.id}>{rayon.icon} {rayon.name}</option>
          ))}
        </select>
      </div>

      {/* Products Grid */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        {filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-rm-text-secondary mb-2">Aucun produit</p>
            <p className="text-sm text-slate-400 mb-4">Commencez par créer votre premier produit pour la Marketplace</p>
            <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-rm-primary">
              <Plus className="w-4 h-4 mr-2" />
              Créer un produit
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="text-left p-4 text-sm font-semibold text-rm-text-secondary">Produit</th>
                  <th className="text-left p-4 text-sm font-semibold text-rm-text-secondary">Rayon</th>
                  <th className="text-left p-4 text-sm font-semibold text-rm-text-secondary">Statut</th>
                  <th className="text-right p-4 text-sm font-semibold text-rm-text-secondary">Prix TTC</th>
                  <th className="text-right p-4 text-sm font-semibold text-rm-text-secondary">Stock</th>
                  <th className="text-center p-4 text-sm font-semibold text-rm-text-secondary">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredProducts.map((product) => (
                  <tr key={product.id} className="hover:bg-slate-50 transition-colors" data-testid={`product-row-${product.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        {product.photos?.[0]?.url ? (
                          <img 
                            src={product.photos[0].url} 
                            alt={product.name}
                            className="w-12 h-12 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                            <Package className="w-6 h-6 text-slate-400" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-rm-text">{product.name}</p>
                          {product.sku && (
                            <p className="text-xs text-rm-text-secondary">REF: {product.sku}</p>
                          )}
                          {product.brand && (
                            <p className="text-xs text-slate-400">{product.brand}</p>
                          )}
                        </div>
                        {product.is_demo && (
                          <span className="px-2 py-0.5 bg-orange-100 text-orange-600 text-xs rounded-full">TEST</span>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <div>
                        <p className="text-sm font-medium text-rm-text">{product.rayon_name}</p>
                        <p className="text-xs text-rm-text-secondary">{product.category_name}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <select
                        value={product.status}
                        onChange={(e) => handleStatusChange(product.id, e.target.value)}
                        className={`text-xs px-2 py-1 rounded-full border-0 cursor-pointer ${statusLabels[product.status]?.color || 'bg-slate-100'}`}
                      >
                        {Object.entries(statusLabels).map(([value, { label }]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-4 text-right">
                      <p className="font-semibold text-rm-text">{formatCurrency(product.price_ttc)}</p>
                      {product.variations?.length > 0 && (
                        <p className="text-xs text-rm-text-secondary">{product.variations.length} variantes</p>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <span className={`text-sm font-medium ${
                        product.stock === -1 ? 'text-blue-600' :
                        product.stock === 0 ? 'text-red-600' :
                        product.stock < 5 ? 'text-yellow-600' : 'text-green-600'
                      }`}>
                        {product.stock === -1 ? 'Sur commande' : product.stock}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => openEditModal(product)}
                          className="p-2 hover:bg-slate-100 rounded-lg text-slate-600"
                          title="Modifier"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDuplicate(product.id)}
                          className="p-2 hover:bg-slate-100 rounded-lg text-slate-600"
                          title="Dupliquer"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(product.id)}
                          className="p-2 hover:bg-red-50 rounded-lg text-red-500"
                          title="Archiver"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Wizard with Preview */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-slate-100 rounded-2xl w-full max-w-7xl shadow-xl max-h-[95vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
              data-testid="product-modal"
            >
              {/* Modal Header */}
              <div className="p-4 bg-white border-b border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-rm-text">
                    {editingProduct ? 'Modifier le produit' : 'Nouveau produit'}
                  </h2>
                  <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Steps Navigation */}
                <div className="flex items-center justify-between overflow-x-auto pb-2">
                  {steps.map((step, index) => {
                    const Icon = step.icon;
                    const isActive = currentStep === step.id;
                    const isCompleted = currentStep > step.id;
                    
                    return (
                      <div key={step.id} className="flex items-center">
                        <button
                          onClick={() => setCurrentStep(step.id)}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors whitespace-nowrap ${
                            isActive ? 'bg-rm-primary text-white' :
                            isCompleted ? 'bg-green-100 text-green-600' :
                            'bg-slate-100 text-slate-500 hover:bg-slate-200'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          <span className="text-sm font-medium hidden md:inline">{step.label}</span>
                        </button>
                        {index < steps.length - 1 && (
                          <div className={`w-4 md:w-8 h-0.5 mx-1 ${isCompleted ? 'bg-green-400' : 'bg-slate-200'}`} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Modal Body with Preview */}
              <div className="flex-1 overflow-hidden flex">
                {/* Form Section */}
                <div className="flex-1 overflow-y-auto p-6 bg-white">
                  {/* Step 1: Informations */}
                  {currentStep === 1 && (
                    <div className="space-y-6" data-testid="step-1-info">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Nom */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-rm-text mb-1">
                            Nom du produit <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            required
                            maxLength={100}
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                            placeholder="Ex: Carrelage sol effet béton 60x60"
                            data-testid="product-name-input"
                          />
                          <p className="text-xs text-slate-400 mt-1">{formData.name.length}/100 caractères</p>
                        </div>

                        {/* Type de transaction */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-rm-text mb-2">
                            Type d'annonce <span className="text-red-500">*</span>
                          </label>
                          <div className="grid grid-cols-3 gap-3">
                            {TRANSACTION_TYPES_PRO.map((type) => (
                              <button
                                key={type.id}
                                type="button"
                                onClick={() => setFormData({ ...formData, transaction_type: type.id })}
                                className={`p-3 rounded-xl border-2 transition-all text-left ${
                                  formData.transaction_type === type.id
                                    ? 'border-current shadow-md'
                                    : 'border-slate-200 hover:border-slate-300'
                                }`}
                                style={formData.transaction_type === type.id ? { borderColor: type.color, backgroundColor: `${type.color}10` } : {}}
                                data-testid={`transaction-type-${type.id}`}
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-lg">{type.icon}</span>
                                  <span className="font-semibold text-sm" style={formData.transaction_type === type.id ? { color: type.color } : {}}>
                                    {type.label}
                                  </span>
                                </div>
                                <p className="text-xs text-slate-500">{type.description}</p>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Rayon */}
                        <div>
                          <label className="block text-sm font-medium text-rm-text mb-1">
                            Rayon <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={formData.rayon_id}
                            onChange={(e) => handleRayonChange(e.target.value)}
                            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                            data-testid="rayon-select"
                          >
                            <option value="">Sélectionner un rayon</option>
                            {rayonsData.map((rayon) => (
                              <option key={rayon.id} value={rayon.id}>
                                {rayon.icon} {rayon.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Sous-rayon */}
                        <div>
                          <label className="block text-sm font-medium text-rm-text mb-1">
                            Sous-rayon <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={formData.sous_rayon_id}
                            onChange={(e) => handleSousRayonChange(e.target.value)}
                            disabled={!formData.rayon_id}
                            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20 disabled:bg-slate-50 disabled:text-slate-400"
                            data-testid="sous-rayon-select"
                          >
                            <option value="">Sélectionner un sous-rayon</option>
                            {selectedRayon?.sous_rayons?.map((sr) => (
                              <option key={sr.id} value={sr.id}>{sr.name}</option>
                            ))}
                          </select>
                        </div>

                        {/* Catégorie */}
                        <div>
                          <label className="block text-sm font-medium text-rm-text mb-1">
                            Catégorie <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={formData.category_id}
                            onChange={(e) => handleCategoryChange(e.target.value)}
                            disabled={!formData.sous_rayon_id}
                            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20 disabled:bg-slate-50 disabled:text-slate-400"
                            data-testid="category-select"
                          >
                            <option value="">Sélectionner une catégorie</option>
                            {selectedSousRayon?.categories?.map((cat) => (
                              <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                          </select>
                        </div>

                        {/* Prix TTC */}
                        <div>
                          <label className="block text-sm font-medium text-rm-text mb-1">
                            Prix TTC (€) <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                              type="number"
                              required
                              min="0"
                              step="0.01"
                              value={formData.price_ttc}
                              onChange={(e) => setFormData({ ...formData, price_ttc: e.target.value })}
                              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                              placeholder="99.99"
                              data-testid="price-input"
                            />
                          </div>
                        </div>

                        {/* Stock */}
                        <div>
                          <label className="block text-sm font-medium text-rm-text mb-1">Stock</label>
                          <div className="flex gap-2">
                            <input
                              type="number"
                              min="-1"
                              value={formData.stock}
                              onChange={(e) => setFormData({ ...formData, stock: parseInt(e.target.value) || 0 })}
                              className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                              placeholder="0"
                              data-testid="stock-input"
                            />
                            <button
                              type="button"
                              onClick={() => setFormData({ ...formData, stock: -1 })}
                              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                formData.stock === -1 
                                  ? 'bg-blue-100 text-blue-600 border-2 border-blue-300' 
                                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                              }`}
                            >
                              Sur commande
                            </button>
                          </div>
                        </div>

                        {/* SKU / Référence */}
                        <div>
                          <label className="block text-sm font-medium text-rm-text mb-1">Référence / SKU</label>
                          <input
                            type="text"
                            value={formData.sku}
                            onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                            placeholder="REF-001"
                          />
                        </div>

                        {/* Marque */}
                        <div>
                          <label className="block text-sm font-medium text-rm-text mb-1">Marque</label>
                          <input
                            type="text"
                            value={formData.brand}
                            onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                            placeholder="Ex: Leroy Merlin"
                          />
                        </div>

                        {/* Description courte */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-rm-text mb-1">
                            Description courte <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            required
                            maxLength={160}
                            value={formData.description_short}
                            onChange={(e) => setFormData({ ...formData, description_short: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                            rows={2}
                            placeholder="Description affichée sur les cartes produits (max 160 caractères)"
                            data-testid="description-short-input"
                          />
                          <p className="text-xs text-slate-400 mt-1">{formData.description_short.length}/160 caractères</p>
                        </div>

                        {/* Description longue */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-rm-text mb-1">
                            Description longue <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            required
                            maxLength={2000}
                            value={formData.description_long}
                            onChange={(e) => setFormData({ ...formData, description_long: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                            rows={5}
                            placeholder="Description détaillée du produit pour la fiche complète"
                            data-testid="description-long-input"
                          />
                          <p className="text-xs text-slate-400 mt-1">{formData.description_long.length}/2000 caractères</p>
                        </div>
                      </div>

                      {/* Caractéristiques techniques */}
                      <div className="border-t border-slate-200 pt-6">
                        <h3 className="text-lg font-semibold text-rm-text mb-4 flex items-center gap-2">
                          <Ruler className="w-5 h-5" />
                          Caractéristiques techniques
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-rm-text mb-1">Poids (kg)</label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={formData.weight_kg}
                              onChange={(e) => setFormData({ ...formData, weight_kg: e.target.value })}
                              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                              placeholder="0.5"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-rm-text mb-1">L (cm)</label>
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              value={formData.dimensions_l}
                              onChange={(e) => setFormData({ ...formData, dimensions_l: e.target.value })}
                              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                              placeholder="60"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-rm-text mb-1">l (cm)</label>
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              value={formData.dimensions_w}
                              onChange={(e) => setFormData({ ...formData, dimensions_w: e.target.value })}
                              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                              placeholder="60"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-rm-text mb-1">H (cm)</label>
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              value={formData.dimensions_h}
                              onChange={(e) => setFormData({ ...formData, dimensions_h: e.target.value })}
                              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                              placeholder="1"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                          <div>
                            <label className="block text-sm font-medium text-rm-text mb-1">Matière</label>
                            <input
                              type="text"
                              value={formData.material}
                              onChange={(e) => setFormData({ ...formData, material: e.target.value })}
                              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                              placeholder="Ex: Grès cérame émaillé"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-rm-text mb-1">Garantie (mois)</label>
                            <input
                              type="number"
                              min="0"
                              value={formData.warranty_months}
                              onChange={(e) => setFormData({ ...formData, warranty_months: e.target.value })}
                              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                              placeholder="24"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Certifications */}
                      <div className="border-t border-slate-200 pt-6">
                        <h3 className="text-lg font-semibold text-rm-text mb-4 flex items-center gap-2">
                          <Award className="w-5 h-5" />
                          Certifications
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(certificationLabels).map(([key, { label, description }]) => (
                            <button
                              key={key}
                              type="button"
                              onClick={() => toggleCertification(key)}
                              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                                formData.certifications.includes(key)
                                  ? 'bg-green-100 text-green-700 border-2 border-green-300'
                                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                              }`}
                              title={description}
                            >
                              {formData.certifications.includes(key) && <Check className="w-4 h-4" />}
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Photos */}
                  {currentStep === 2 && (
                    <div className="space-y-6" data-testid="step-2-photos">
                      <div>
                        <h3 className="text-lg font-semibold text-rm-text">Photos du produit</h3>
                        <p className="text-sm text-rm-text-secondary">
                          Glissez-déposez vos images ou cliquez pour sélectionner. 
                          Compression automatique pour un chargement rapide.
                        </p>
                      </div>

                      <ImageUploader
                        images={formData.photos}
                        onChange={(newPhotos) => setFormData(prev => ({ ...prev, photos: newPhotos }))}
                        maxImages={5}
                        folder="products"
                      />
                    </div>
                  )}

                  {/* Step 3: Déclinaisons pré-remplies */}
                  {currentStep === 3 && (
                    <div className="space-y-6" data-testid="step-3-declinaisons">
                      <div>
                        <h3 className="text-lg font-semibold text-rm-text">Déclinaisons du produit</h3>
                        <p className="text-sm text-rm-text-secondary">
                          {formData.rayon_id 
                            ? `Déclinaisons pré-remplies pour "${formData.rayon_name}". Activez celles qui s'appliquent à votre produit.`
                            : 'Sélectionnez d\'abord un rayon (Étape 1) pour voir les déclinaisons disponibles.'
                          }
                        </p>
                      </div>

                      {!formData.rayon_id ? (
                        <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50">
                          <Tag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                          <p className="text-rm-text-secondary mb-4">Aucun rayon sélectionné</p>
                          <Button type="button" variant="outline" size="sm" onClick={() => setCurrentStep(1)}>
                            Retour à l'étape 1
                          </Button>
                        </div>
                      ) : formData.declinaisons.length === 0 ? (
                        <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50">
                          <Tag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                          <p className="text-rm-text-secondary">Aucune déclinaison disponible pour ce rayon</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {/* Liste des déclinaisons */}
                          {formData.declinaisons
                            .filter(d => showAllDeclinaisons || d.isVisible)
                            .map((declinaison) => (
                            <div 
                              key={declinaison.id} 
                              className={`rounded-xl border-2 transition-all ${
                                declinaison.enabled 
                                  ? 'border-rm-primary bg-white shadow-sm' 
                                  : 'border-slate-200 bg-slate-50'
                              }`}
                            >
                              {/* Header avec toggle */}
                              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                                <div className="flex items-center gap-3">
                                  <button
                                    type="button"
                                    onClick={() => handleDeclinaisonToggle(declinaison.id)}
                                    className={`relative w-12 h-6 rounded-full transition-colors ${
                                      declinaison.enabled ? 'bg-rm-primary' : 'bg-slate-300'
                                    }`}
                                    data-testid={`toggle-${declinaison.id}`}
                                  >
                                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                                      declinaison.enabled ? 'translate-x-7' : 'translate-x-1'
                                    }`} />
                                  </button>
                                  <span className={`font-medium ${declinaison.enabled ? 'text-rm-text' : 'text-slate-500'}`}>
                                    {declinaison.name}
                                  </span>
                                </div>
                                {declinaison.enabled && declinaison.selectedValues.length > 0 && (
                                  <span className="text-xs bg-rm-primary/10 text-rm-primary px-2 py-1 rounded-full font-medium">
                                    {declinaison.selectedValues.length} sélectionné{declinaison.selectedValues.length > 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>

                              {/* Chips - affichés seulement si activé */}
                              {declinaison.enabled && (
                                <div className="p-4">
                                  <div className="flex flex-wrap gap-2 mb-3">
                                    {declinaison.chips.map(chip => {
                                      const isSelected = declinaison.selectedValues.includes(chip);
                                      return (
                                        <button
                                          key={chip}
                                          type="button"
                                          onClick={() => handleChipToggle(declinaison.id, chip)}
                                          className={`px-3 py-1.5 text-sm rounded-full border-2 transition-all ${
                                            isSelected 
                                              ? 'bg-[#F97316] border-[#F97316] text-white font-medium' 
                                              : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                                          }`}
                                          data-testid={`chip-${declinaison.id}-${chip}`}
                                        >
                                          {isSelected && <Check className="w-3 h-3 inline mr-1" />}
                                          {chip}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  
                                  {/* Valeurs personnalisées ajoutées */}
                                  {declinaison.selectedValues.filter(v => !declinaison.chips.includes(v)).length > 0 && (
                                    <div className="flex flex-wrap gap-2 mb-3 pt-2 border-t border-slate-100">
                                      <span className="text-xs text-slate-500 w-full mb-1">Valeurs personnalisées :</span>
                                      {declinaison.selectedValues
                                        .filter(v => !declinaison.chips.includes(v))
                                        .map(customVal => (
                                          <button
                                            key={customVal}
                                            type="button"
                                            onClick={() => handleChipToggle(declinaison.id, customVal)}
                                            className="px-3 py-1.5 text-sm rounded-full bg-[#F97316] border-2 border-[#F97316] text-white font-medium"
                                          >
                                            <Check className="w-3 h-3 inline mr-1" />
                                            {customVal}
                                          </button>
                                        ))
                                      }
                                    </div>
                                  )}

                                  {/* Champ "Autre valeur" */}
                                  <div className="flex gap-2 pt-2 border-t border-slate-100">
                                    <input
                                      type="text"
                                      value={declinaison.customValue || ''}
                                      onChange={(e) => handleCustomValueChange(declinaison.id, e.target.value)}
                                      onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          handleAddCustomValue(declinaison.id, declinaison.customValue);
                                        }
                                      }}
                                      placeholder="Autre valeur..."
                                      className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20 focus:border-rm-primary"
                                    />
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleAddCustomValue(declinaison.id, declinaison.customValue)}
                                      disabled={!declinaison.customValue?.trim()}
                                    >
                                      <Plus className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}

                          {/* Bouton "Voir plus de déclinaisons" */}
                          {!showAllDeclinaisons && formData.declinaisons.length > MAX_DECLINAISONS_DEFAULT && (
                            <button
                              type="button"
                              onClick={handleShowAllDeclinaisons}
                              className="w-full py-3 text-sm text-rm-primary font-medium border-2 border-dashed border-rm-primary/30 rounded-xl hover:bg-rm-primary/5 transition-colors flex items-center justify-center gap-2"
                            >
                              <Plus className="w-4 h-4" />
                              Voir {formData.declinaisons.length - MAX_DECLINAISONS_DEFAULT} déclinaison{formData.declinaisons.length - MAX_DECLINAISONS_DEFAULT > 1 ? 's' : ''} supplémentaire{formData.declinaisons.length - MAX_DECLINAISONS_DEFAULT > 1 ? 's' : ''}
                            </button>
                          )}

                          {/* Résumé des déclinaisons activées */}
                          {formData.declinaisons.some(d => d.enabled && d.selectedValues.length > 0) && (
                            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                              <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                                <CheckCircle className="w-5 h-5" />
                                Déclinaisons activées
                              </h4>
                              <div className="space-y-2">
                                {formData.declinaisons
                                  .filter(d => d.enabled && d.selectedValues.length > 0)
                                  .map(d => (
                                    <div key={d.id} className="text-sm text-green-700">
                                      <span className="font-medium">{d.name} :</span>{' '}
                                      {d.selectedValues.join(' · ')}
                                    </div>
                                  ))
                                }
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 4: Livraison */}
                  {currentStep === 4 && (
                    <div className="space-y-6" data-testid="step-4-delivery">
                      <div>
                        <h3 className="text-lg font-semibold text-rm-text mb-2">Options de livraison</h3>
                        <p className="text-sm text-rm-text-secondary">Configurez les modes de livraison disponibles.</p>
                      </div>

                      {/* Livraison toggle */}
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({
                            ...prev,
                            delivery: { ...prev.delivery, available: !prev.delivery.available }
                          }))}
                          className={`relative w-12 h-6 rounded-full transition-colors ${
                            formData.delivery.available ? 'bg-rm-primary' : 'bg-slate-300'
                          }`}
                        >
                          <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            formData.delivery.available ? 'translate-x-7' : 'translate-x-1'
                          }`} />
                        </button>
                        <span className="text-sm font-medium text-rm-text">Livraison disponible</span>
                      </div>

                      {formData.delivery.available && (
                        <>
                          {/* Modes */}
                          <div>
                            <label className="block text-sm font-medium text-rm-text mb-3">Modes de livraison</label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {Object.entries(deliveryModeLabels).map(([key, { label, icon, description }]) => (
                                <button
                                  key={key}
                                  type="button"
                                  onClick={() => toggleDeliveryMode(key)}
                                  className={`p-4 rounded-lg border-2 text-left transition-colors ${
                                    formData.delivery.modes.includes(key)
                                      ? 'border-rm-primary bg-rm-primary/5'
                                      : 'border-slate-200 hover:border-slate-300'
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <span className="text-2xl">{icon}</span>
                                    <div className="flex-1">
                                      <p className="font-medium text-rm-text">{label}</p>
                                      <p className="text-xs text-rm-text-secondary">{description}</p>
                                    </div>
                                    {formData.delivery.modes.includes(key) && <Check className="w-5 h-5 text-rm-primary" />}
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Délai */}
                          <div>
                            <label className="block text-sm font-medium text-rm-text mb-1">Délai estimé</label>
                            <input
                              type="text"
                              value={formData.delivery.estimated_delay}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                delivery: { ...prev.delivery, estimated_delay: e.target.value }
                              }))}
                              className="w-full max-w-md px-4 py-2 border border-slate-200 rounded-lg"
                              placeholder="Ex: 2-5 jours ouvrés"
                            />
                          </div>

                          {/* Frais */}
                          <div>
                            <label className="block text-sm font-medium text-rm-text mb-3">Frais de livraison</label>
                            <div className="flex flex-wrap gap-2 mb-4">
                              {Object.entries(shippingCostTypeLabels).map(([key, label]) => (
                                <button
                                  key={key}
                                  type="button"
                                  onClick={() => setFormData(prev => ({
                                    ...prev,
                                    delivery: { ...prev.delivery, cost_type: key }
                                  }))}
                                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                    formData.delivery.cost_type === key
                                      ? 'bg-rm-primary text-white'
                                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                  }`}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>

                            {formData.delivery.cost_type === 'fixed' && (
                              <div className="max-w-xs">
                                <label className="block text-xs font-medium text-rm-text-secondary mb-1">Montant (€)</label>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={formData.delivery.cost_amount}
                                  onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    delivery: { ...prev.delivery, cost_amount: e.target.value }
                                  }))}
                                  className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                                  placeholder="5.90"
                                />
                              </div>
                            )}
                          </div>

                          {/* Zones */}
                          <div>
                            <label className="block text-sm font-medium text-rm-text mb-3">Zones de livraison</label>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(shippingZoneLabels).map(([key, label]) => (
                                <button
                                  key={key}
                                  type="button"
                                  onClick={() => toggleShippingZone(key)}
                                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                                    formData.delivery.zones.includes(key)
                                      ? 'bg-green-100 text-green-700 border-2 border-green-300'
                                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                  }`}
                                >
                                  {formData.delivery.zones.includes(key) && <Check className="w-4 h-4" />}
                                  {label}
                                </button>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Step 5: SEO */}
                  {currentStep === 5 && (
                    <div className="space-y-6" data-testid="step-5-seo">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-rm-text">Référencement (SEO)</h3>
                          <p className="text-sm text-rm-text-secondary">Optimisez la visibilité de votre produit sur Google</p>
                        </div>
                        <Button type="button" variant="outline" size="sm" onClick={generateSEO}>
                          <Sparkles className="w-4 h-4 mr-1" />
                          Générer auto
                        </Button>
                      </div>

                      {/* Titre SEO */}
                      <div>
                        <label className="block text-sm font-medium text-rm-text mb-1">
                          Titre SEO
                          <span className={`ml-2 text-xs ${
                            formData.seo_title.length >= 30 && formData.seo_title.length <= 60 ? 'text-green-600' : 'text-yellow-600'
                          }`}>
                            ({formData.seo_title.length}/60)
                          </span>
                        </label>
                        <input
                          type="text"
                          maxLength={60}
                          value={formData.seo_title}
                          onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                          placeholder={formData.name || 'Titre optimisé pour Google (30-60 caractères)'}
                        />
                        <p className="text-xs text-slate-400 mt-1">Apparaît en bleu dans les résultats Google</p>
                      </div>

                      {/* Meta description */}
                      <div>
                        <label className="block text-sm font-medium text-rm-text mb-1">
                          Meta description
                          <span className={`ml-2 text-xs ${
                            formData.seo_description.length >= 120 && formData.seo_description.length <= 160 ? 'text-green-600' : 'text-yellow-600'
                          }`}>
                            ({formData.seo_description.length}/160)
                          </span>
                        </label>
                        <textarea
                          maxLength={160}
                          value={formData.seo_description}
                          onChange={(e) => setFormData({ ...formData, seo_description: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                          rows={3}
                          placeholder={formData.description_short || 'Description pour les résultats de recherche (120-160 caractères)'}
                        />
                        <p className="text-xs text-slate-400 mt-1">Apparaît en gris sous le titre dans Google</p>
                      </div>

                      {/* URL personnalisée */}
                      <div>
                        <label className="block text-sm font-medium text-rm-text mb-1">URL personnalisée (slug)</label>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-500">abracadabati.com/marketplace/</span>
                          <input
                            type="text"
                            value={formData.seo_slug}
                            onChange={(e) => setFormData({ ...formData, seo_slug: generateSlug(e.target.value) })}
                            className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                            placeholder={generateSlug(formData.name) || 'url-du-produit'}
                          />
                        </div>
                      </div>

                      {/* Mots-clés */}
                      <div>
                        <label className="block text-sm font-medium text-rm-text mb-1">
                          Mots-clés / Tags
                          <span className="ml-2 text-xs text-slate-500">({formData.seo_keywords.length} mot(s)-clé(s))</span>
                        </label>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {formData.seo_keywords.map((keyword, i) => (
                            <span key={i} className="inline-flex items-center gap-1 px-3 py-1 bg-rm-primary/10 text-rm-primary rounded-full text-sm">
                              <Hash className="w-3 h-3" />
                              {keyword}
                              <button type="button" onClick={() => handleRemoveKeyword(keyword)} className="hover:text-red-500">
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                        <form onSubmit={handleAddKeyword} className="flex gap-2">
                          <input
                            type="text"
                            value={keywordInput}
                            onChange={(e) => setKeywordInput(e.target.value)}
                            className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rm-primary/20"
                            placeholder="Ajouter un mot-clé..."
                          />
                          <Button type="submit" variant="outline">Ajouter</Button>
                        </form>
                      </div>
                    </div>
                  )}

                  {/* Step 6: Publication */}
                  {currentStep === 6 && (
                    <div className="space-y-6" data-testid="step-6-publish">
                      <div>
                        <h3 className="text-lg font-semibold text-rm-text mb-2">Publication</h3>
                        <p className="text-sm text-rm-text-secondary">Choisissez le statut de votre produit.</p>
                      </div>

                      {/* Récapitulatif */}
                      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                        <h4 className="font-medium text-rm-text mb-3">Récapitulatif</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-rm-text-secondary">Nom:</span>
                            <p className="font-medium text-rm-text truncate">{formData.name || '-'}</p>
                          </div>
                          <div>
                            <span className="text-rm-text-secondary">Prix:</span>
                            <p className="font-medium text-rm-text">{formData.price_ttc ? formatCurrency(formData.price_ttc) : '-'}</p>
                          </div>
                          <div>
                            <span className="text-rm-text-secondary">Photos:</span>
                            <p className="font-medium text-rm-text">{formData.photos.length}</p>
                          </div>
                          <div>
                            <span className="text-rm-text-secondary">Déclinaisons:</span>
                            <p className="font-medium text-rm-text">
                              {formData.declinaisons.filter(d => d.enabled && d.selectedValues.length > 0).length}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Statut */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, status: 'draft' }))}
                          className={`p-4 rounded-lg border-2 text-left transition-colors ${
                            formData.status === 'draft'
                              ? 'border-slate-400 bg-slate-50'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                              <FileText className="w-5 h-5 text-slate-600" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-rm-text">Brouillon</p>
                              <p className="text-xs text-rm-text-secondary">Non visible sur la Marketplace</p>
                            </div>
                            {formData.status === 'draft' && <Check className="w-5 h-5 text-slate-600" />}
                          </div>
                        </button>

                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, status: 'active' }))}
                          className={`p-4 rounded-lg border-2 text-left transition-colors ${
                            formData.status === 'active'
                              ? 'border-green-400 bg-green-50'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-green-200 flex items-center justify-center">
                              <Eye className="w-5 h-5 text-green-600" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-rm-text">Publier maintenant</p>
                              <p className="text-xs text-rm-text-secondary">Visible sur la Marketplace</p>
                            </div>
                            {formData.status === 'active' && <Check className="w-5 h-5 text-green-600" />}
                          </div>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Preview Panel */}
                <div className="hidden lg:block w-80 bg-slate-50 border-l border-slate-200 p-4 overflow-y-auto">
                  <div className="sticky top-0">
                    {/* Preview Toggle */}
                    <div className="flex items-center gap-2 mb-4">
                      <button
                        onClick={() => setPreviewMode('card')}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          previewMode === 'card' ? 'bg-white shadow text-rm-text' : 'text-slate-500 hover:text-rm-text'
                        }`}
                      >
                        Card
                      </button>
                      <button
                        onClick={() => setPreviewMode('seo')}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          previewMode === 'seo' ? 'bg-white shadow text-rm-text' : 'text-slate-500 hover:text-rm-text'
                        }`}
                      >
                        SEO
                      </button>
                    </div>

                    {previewMode === 'card' ? (
                      <div className="space-y-4">
                        <h4 className="text-sm font-semibold text-rm-text">Aperçu Marketplace</h4>
                        <ProductCardPreview formData={formData} />
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <SEOScoreIndicator formData={formData} />
                        <GooglePreview formData={formData} />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-4 border-t border-slate-200 bg-white">
                <div className="flex items-center justify-between">
                  <div>
                    {currentStep > 1 && (
                      <Button type="button" variant="outline" onClick={() => setCurrentStep(currentStep - 1)}>
                        Précédent
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Annuler</Button>
                    {currentStep < 6 ? (
                      <Button type="button" onClick={() => setCurrentStep(currentStep + 1)} className="bg-rm-primary">
                        Suivant
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        onClick={handleSubmit}
                        disabled={saving}
                        className="bg-rm-primary"
                        data-testid="submit-product-btn"
                      >
                        {saving ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Enregistrement...
                          </>
                        ) : (
                          editingProduct ? 'Mettre à jour' : 'Créer le produit'
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
